import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { getIdToken } from "../auth/AuthProvider.js";
import type {
  JobStatusUpdateBody,
  OpportunityResult,
  OpportunityView,
  QuoteView,
  SubmitQuoteBody,
  ThreadMessage,
} from "../opportunities/api.js";

/**
 * Client for the signed-in portal API (/portal/*, build-plan unit 15).
 * Unlike opportunities/api.ts (magic-link, x-invitation-token header),
 * these routes are JWT-authorized — the Cognito ID token rides in the
 * Authorization header, attached here on every call.
 */

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly body: unknown,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

export interface ApiOptions extends Omit<RequestInit, "body"> {
  json?: unknown;
}

export async function portalFetch<T = unknown>(
  path: string,
  options: ApiOptions = {},
): Promise<T> {
  const baseUrl = import.meta.env.VITE_API_BASE_URL;
  if (!baseUrl) {
    throw new Error("VITE_API_BASE_URL is not set in this build.");
  }

  const token = await getIdToken();
  const headers = new Headers(options.headers);
  if (token) headers.set("Authorization", `Bearer ${token}`);

  const init: RequestInit = { ...options, headers };
  if (options.json !== undefined) {
    headers.set("Content-Type", "application/json");
    init.body = JSON.stringify(options.json);
  }

  const url = baseUrl.replace(/\/$/, "") + (path.startsWith("/") ? path : `/${path}`);
  const res = await fetch(url, init);
  const text = await res.text();
  const parsed: unknown = text ? JSON.parse(text) : null;
  if (!res.ok) {
    throw new ApiError(`API ${res.status} ${res.statusText}`, res.status, parsed);
  }
  return parsed as T;
}

export type ContractorStatus = "LEAD" | "PENDING_REVIEW" | "APPROVED" | "SUSPENDED";

/**
 * GET /portal/me. The server auto-claims on the first call when the
 * verified login email matches an unclaimed contractor record (unit 16);
 * `contractor` stays null for true strangers, who apply via
 * POST /portal/application. `claimConflict` means the email matches a
 * record already linked to a different login.
 */
export interface PortalMe {
  email: string;
  givenName?: string;
  contractor: {
    contractorId: string;
    businessName: string;
    status: ContractorStatus;
  } | null;
  claimConflict?: boolean;
}

export function usePortalMe(enabled = true) {
  return useQuery<PortalMe>({
    queryKey: ["portal", "me"],
    enabled,
    retry: 1,
    queryFn: () => portalFetch<PortalMe>("/portal/me"),
  });
}

export interface ServiceArea {
  postalCodes: string[];
  cities: string[];
}

/** GET/PUT /portal/profile — the contractor's own registry record. */
export interface PortalProfile {
  contractorId: string;
  status: ContractorStatus;
  businessName: string;
  contactName: string;
  email: string;
  phone: string;
  website?: string;
  serviceCategories: string[];
  serviceArea: ServiceArea;
  yearsExperience: number;
  licenseInfo?: string;
  insuranceInfo?: string;
  about?: string;
  messageAlerts: "immediate" | "digest" | "off";
  emailSuppressed: boolean;
}

export function usePortalProfile(enabled = true) {
  return useQuery<PortalProfile>({
    queryKey: ["portal", "profile"],
    enabled,
    retry: 1,
    queryFn: () => portalFetch<PortalProfile>("/portal/profile"),
  });
}

export interface ProfileUpdateBody {
  businessName?: string;
  contactName?: string;
  phone?: string;
  website?: string;
  serviceCategories?: string[];
  serviceArea?: ServiceArea;
  licenseInfo?: string;
  insuranceInfo?: string;
  about?: string;
  messageAlerts?: "immediate" | "digest" | "off";
}

export function useUpdateProfile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: ProfileUpdateBody) =>
      portalFetch<PortalProfile>("/portal/profile", { method: "PUT", json: body }),
    onSuccess: (profile) => {
      qc.setQueryData(["portal", "profile"], profile);
      void qc.invalidateQueries({ queryKey: ["portal", "me"] });
    },
  });
}

export interface SocialLinksRecord {
  facebook?: string;
  instagram?: string;
  linkedin?: string;
  youtube?: string;
  twitter?: string;
}

export interface LeadDelivery {
  email: boolean;
  sms: boolean;
}

export interface BusinessRules {
  minJobSize?: string;
  freeEstimates?: boolean;
  emergencyCalls?: boolean;
  residential?: boolean;
  commercial?: boolean;
}

export interface ApplicationBody {
  businessName: string;
  contactName: string;
  email: string;
  phone: string;
  website?: string;
  serviceCategories: string[];
  serviceArea: ServiceArea;
  yearsExperience: number;
  licenseInfo?: string;
  insuranceInfo?: string;
  about?: string;
  // AI-onboarding extras (all optional + additive).
  services?: string[];
  capabilities?: string[];
  logoUrl?: string;
  photos?: string[];
  socialLinks?: SocialLinksRecord;
  leadDelivery?: LeadDelivery;
  businessRules?: BusinessRules;
  licenseNumber?: string;
  insuranceCertKey?: string;
  insuranceCertName?: string;
}

export function useSubmitApplication() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: ApplicationBody) =>
      portalFetch("/portal/application", { method: "POST", json: body }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["portal"] });
    },
  });
}

// --- Verification cert upload (onboarding Step 5) --------------------------

interface CertPresignResponse {
  uploadUrl: string;
  key: string;
  expiresInSeconds: number;
}

const CERT_MIME_EXT: Record<string, string> = {
  "application/pdf": "pdf",
  "image/jpeg": "jpg",
  "image/png": "png",
  "image/webp": "webp",
};
export const ACCEPTED_CERT_TYPES = Object.keys(CERT_MIME_EXT).join(",");
export const MAX_CERT_BYTES = 10 * 1024 * 1024;

/**
 * Upload an insurance certificate: ask the API for a presigned PUT, then upload
 * the file straight to S3. Returns the stored object key (kept on the profile)
 * and the original filename (display only).
 */
export async function uploadInsuranceCert(file: File): Promise<{ key: string; name: string }> {
  if (!CERT_MIME_EXT[file.type]) throw new Error("Use a PDF or image (JPG, PNG, WEBP).");
  if (file.size > MAX_CERT_BYTES) throw new Error("That file is too large (max 10 MB).");
  const presign = await portalFetch<CertPresignResponse>("/portal/verification/cert/presign", {
    method: "POST",
    json: { contentType: file.type, fileName: file.name },
  });
  const put = await fetch(presign.uploadUrl, {
    method: "PUT",
    headers: { "content-type": file.type },
    body: file,
  });
  if (!put.ok) throw new Error("Upload failed — please try again.");
  return { key: presign.key, name: file.name };
}

// ---------------------------------------------------------------------------
// Dashboard (unit 17). Same response shapes as the magic-link surface —
// the API serves both from one domain layer; only the authorization differs.

export type Outcome = "open" | "won" | "filled" | "closed";

export interface PortalOpportunityListItem {
  invitationId: string;
  rfqId: string;
  invitationStatus: "INVITED" | "VIEWED" | "QUOTED" | "REVOKED" | "EXPIRED";
  createdAt: string;
  outcome: Outcome;
  opportunity: {
    serviceCategory: string;
    urgency: string;
    customerFirstName: string;
    location: { city: string; state: string; postalCode: string };
  } | null;
  quote: QuoteView | null;
  lastMessage: { sender: "customer" | "contractor"; sentAt: string } | null;
}

export function usePortalOpportunities(enabled = true) {
  return useQuery({
    queryKey: ["portal", "opportunities"],
    enabled,
    retry: 1,
    queryFn: () => portalFetch<{ items: PortalOpportunityListItem[] }>("/portal/opportunities"),
  });
}

function stateForStatus(status: number): "not_found" | "closed" | "error" {
  if (status === 404) return "not_found";
  if (status === 410) return "closed";
  return "error";
}

/** Detail view — the unit-9/10 OpportunityResult shape, sans "expired". */
export function usePortalOpportunity(invitationId: string | undefined) {
  return useQuery<OpportunityResult>({
    queryKey: ["portal", "opportunity", invitationId],
    enabled: Boolean(invitationId),
    retry: false,
    queryFn: async () => {
      try {
        return await portalFetch<OpportunityView>(`/portal/opportunities/${invitationId!}`);
      } catch (err) {
        if (err instanceof ApiError) return { state: stateForStatus(err.status) };
        throw err;
      }
    },
  });
}

export function usePortalSubmitQuote(invitationId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: SubmitQuoteBody) =>
      portalFetch(`/portal/opportunities/${invitationId!}/quotes`, { method: "POST", json: body }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["portal", "opportunity", invitationId] });
      void qc.invalidateQueries({ queryKey: ["portal", "opportunities"] });
    },
  });
}

/** Job lifecycle from the portal (unit 18). */
export function usePortalUpdateJobStatus(invitationId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: JobStatusUpdateBody) =>
      portalFetch(`/portal/opportunities/${invitationId!}/job-status`, {
        method: "POST",
        json: body,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["portal", "opportunity", invitationId] });
      void qc.invalidateQueries({ queryKey: ["portal", "opportunities"] });
    },
  });
}

export function usePortalMessages(invitationId: string | undefined) {
  return useQuery({
    queryKey: ["portal", "opportunity", invitationId, "messages"],
    enabled: Boolean(invitationId),
    retry: false,
    refetchInterval: 30_000,
    queryFn: async () => {
      try {
        return await portalFetch<{ threadOpen: boolean; messages: ThreadMessage[] }>(
          `/portal/opportunities/${invitationId!}/messages`,
        );
      } catch {
        return { threadOpen: false, messages: [] as ThreadMessage[] };
      }
    },
  });
}

export function usePortalSendMessage(invitationId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: string) =>
      portalFetch(`/portal/opportunities/${invitationId!}/messages`, {
        method: "POST",
        json: { body },
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["portal", "opportunity", invitationId, "messages"] });
      void qc.invalidateQueries({ queryKey: ["portal", "opportunities"] });
    },
  });
}

// ---------------------------------------------------------------------------
// Client-side unread state (unit 17): per-invitation last-seen timestamps in
// localStorage — no read-receipt events by design (unit 14 decision). Only
// the SIGNED-IN portal writes these; the magic-link page stores nothing.

const LAST_SEEN_PREFIX = "qcpro.thread-seen.";

export function markThreadSeen(invitationId: string): void {
  try {
    window.localStorage.setItem(LAST_SEEN_PREFIX + invitationId, new Date().toISOString());
  } catch {
    // Storage full/blocked — unread dots degrade gracefully.
  }
}

export function isThreadUnread(item: PortalOpportunityListItem): boolean {
  if (!item.lastMessage || item.lastMessage.sender !== "customer") return false;
  try {
    const seen = window.localStorage.getItem(LAST_SEEN_PREFIX + item.invitationId);
    return !seen || seen < item.lastMessage.sentAt;
  } catch {
    return false;
  }
}
